import React, { useEffect, useState } from "react";
import { IconFilter } from "@tabler/icons-react";
import { TransactionType } from "../../models/enums";
import { useMediaQuery } from "@mantine/hooks";
import { Group, Switch } from "@mantine/core";
import styles from "./Filter.module.scss";
import { RenderCombobox } from "./RenderCombobox.component";
import { MobileModalLayout } from "../ModalLayout/MobileModalLayout";
import { transactionTypeLabels } from "../../pages/transactionDetails/TransactionConfig";

interface FilterProps {
  txType: TransactionType | null;
  setTxType: (type: TransactionType | null) => void;
  showEmptyBlocks: boolean;
  setShowEmptyBlocks: (showEmptyBlocks: boolean) => void;
  setPage: (page: number) => void;
}

interface FilterControlsProps {
  txType: TransactionType | null;
  setTxType: (type: TransactionType | null) => void;
  showEmptyBlocks: boolean;
  setShowEmptyBlocks: (showEmptyBlocks: boolean) => void;
  setPage: (page: number) => void;
  fullWidth?: boolean;
}

function FilterControls({
  txType,
  setTxType,
  showEmptyBlocks,
  setShowEmptyBlocks,
  setPage,
  fullWidth,
}: FilterControlsProps) {
  const commonSwitchStyles = {
    track: {
      backgroundColor: showEmptyBlocks ? "var(--brand-color)" : "",
    },
    label: {
      color: "var(--mid-dark)",
      fontSize: "14px",
    },
  } as const;

  return (
    <>
      <RenderCombobox
        label="Type"
        options={transactionTypeLabels}
        selectedValue={txType}
        setSelected={(v) => {
          setPage(1); // pozor: pri mobilnem draught state-u bo to no-op
          setTxType(v as TransactionType | null);
        }}
        setPage={setPage}
        width={fullWidth ? "100%" : 130}
        dropdownWidth={fullWidth ? 180 : 250}
      />

        <RenderCombobox
      label="Empty blocks"
      options={{
        true: "Yes",
        false: "No",
      }}
      showClearButton={false}
      selectedValue={showEmptyBlocks ? "true" : "false"}
      setSelected={(v) =>{ 
        setPage(1);
        setShowEmptyBlocks(v === "true" ? true : false)
      }}
       width={fullWidth ? "100%" : 100}
        dropdownWidth={100}
    />
    </>
  );
}

export function Filter(props: FilterProps) {
  const { txType, setTxType, showEmptyBlocks, setShowEmptyBlocks, setPage } =
    props;
  const isMobile = useMediaQuery("(max-width: 860px)");
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);

  // mobile draft state -> permanenty saved only when onApply is called
  const [draftTxType, setDraftTxType] = useState<TransactionType | null>(
    txType
  );
  const [draftShowEmptyBlocks, setDraftShowEmptyBlocks] =
    useState(showEmptyBlocks);

  // close menu
  useEffect(() => {
    if (!isMobile) {
      setMobileMenuVisible(false);
    }
  }, [isMobile]);

  const openMobileFilter = () => {
    setDraftTxType(txType);
    setDraftShowEmptyBlocks(showEmptyBlocks);
    setMobileMenuVisible(true);
  };

  if (isMobile) {
    return (
      <>
        {!mobileMenuVisible && (
          <div onClick={() => openMobileFilter()}>
            <IconFilter style={{ cursor: "pointer" }} size={20} color="gray" />
          </div>
        )}
        {mobileMenuVisible && (
          <div className={styles.mobileFilter}>
            <MobileModalLayout
              title="Set filters"
              onClose={() => setMobileMenuVisible(false)}
              onApply={() => {
                setTxType(draftTxType);
                setShowEmptyBlocks(draftShowEmptyBlocks);
                setPage(1);
                setMobileMenuVisible(false);
              }}
              applyLabel="Apply values"
            >
              <FilterControls
                txType={draftTxType}
                setTxType={setDraftTxType}
                showEmptyBlocks={draftShowEmptyBlocks}
                setShowEmptyBlocks={setDraftShowEmptyBlocks}
                // do nothing on draft
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                setPage={() => {}}
                fullWidth
              />
            </MobileModalLayout>
          </div>
        )}
      </>
    );
  }

  // Desktop
  return (
    <Group className={styles.container} gap="xs">
      <IconFilter size={20} color="gray" />

      <FilterControls
        txType={txType}
        setTxType={setTxType}
        showEmptyBlocks={showEmptyBlocks}
        setShowEmptyBlocks={setShowEmptyBlocks}
        setPage={setPage}
      />
    </Group>
  );
}
